import {
  addDays,
  differenceInMinutes,
  differenceInSeconds,
  endOfWeek,
  format,
  formatISO,
  getDay,
  isAfter,
  isWithinInterval,
  parse,
  parseISO,
  setHours,
  setMinutes,
  startOfDay,
  startOfWeek
} from 'date-fns';

import { AM, PM, SERVER_DATE_FORMAT } from '@nw/shared/config';

export const getTimeInMinutesAndSecs = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;
  let minuteString = `${minutes}`;
  if (minutes < 10) {
    minuteString = `0${minutes}`;
  }
  let secondString = `${seconds}`;
  if (seconds < 10) {
    secondString = `0${seconds}`;
  }
  return `${minuteString}:${secondString}`;
};

export const getTimeInHrsMinsSecs = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  let hourString = `${hours}`;
  if (hours < 10) {
    hourString = `0${hours}`;
  }
  let minuteString = `${minutes}`;
  if (minutes < 10) {
    minuteString = `0${minutes}`;
  }
  let secondString = `${seconds}`;
  if (seconds < 10) {
    secondString = `0${seconds}`;
  }
  return `${hourString} hr &nbsp;: &nbsp;${minuteString} min &nbsp;: &nbsp;${secondString} sec`;
};

export const getFormattedDateTimeFromSerevrDateTimeApi = (
  networkDateTimeObject
) => {
  let formattedDateTime = networkDateTimeObject.replace(
    '"network_time":  ',
    '"network_time":  "'
  );
  formattedDateTime = formattedDateTime.replace('\n}', '"\n}');
  formattedDateTime = JSON.parse(formattedDateTime);
  const dateObj = parse(
    formattedDateTime.network_time,
    'dd/MMM/yyyy:HH:mm:ss XXXX',
    new Date()
  );

  const formattedTime = format(dateObj, 'yyyy-MM-dd HH:mm:ss');

  return formattedTime;
};

export function getServerDateFormat(date: Date | number): string {
  return format(date, SERVER_DATE_FORMAT);
}

export const getTodayDate = (): Date => {
  return new Date();
};

export const getFormattedDate = (
  date: Date | string | null,
  formatToConvert: string
): string | null => {
  if (date && typeof date === 'string') {
    const updatedDateFormat = new Date(date);
    return format(updatedDateFormat, formatToConvert);
  } else if (date && date instanceof Date) {
    return format(date, formatToConvert);
  }
  return null;
};

export const getFormatTimeWithMinutes = (timeString: string): string => {
  const timeMatch = timeString.match(/(\d{1,2})\s*(AM|PM)/i);

  if (!timeMatch) {
    throw new Error('Invalid time format');
  }

  const hours = timeMatch[1];
  const amPm = timeMatch[2].toUpperCase();

  return `${hours}:00${amPm}`;
};

const parseTimeAndFormat = (
  webinarDateTime: string,
  timeString: string
): string => {
  const timeMatch = timeString.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i);

  if (!timeMatch) {
    throw new Error('Invalid time format');
  }

  let hours = parseInt(timeMatch[1], 10);
  const minutes = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
  const amPm = timeMatch[3].toUpperCase();
  if (amPm === AM && hours === 12) {
    hours = 0;
  } else if (amPm === PM && hours !== 12) {
    hours += 12;
  }

  const parsedWebinarDateTime = parseISO(webinarDateTime);
  const webinarDateTimeWithHoursSet = setHours(parsedWebinarDateTime, hours);
  const webinarDateTimeWithMinutesSet = setMinutes(
    webinarDateTimeWithHoursSet,
    minutes
  );

  return formatISO(webinarDateTimeWithMinutesSet);
};

export const getWebinarDateTimeFromSelectedSlot = (
  webinarDateTime: string,
  preferredSlot: string
): string => {
  const cleanedSlot = preferredSlot.trim().replace(/\s+/g, ' ');

  // Check if this is a time range with AM/PM (e.g., "6:00PM - 9:00PM")
  if (cleanedSlot.match(/\d{1,2}:\d{2}(AM|PM)\s*-\s*\d{1,2}:\d{2}(AM|PM)/i)) {
    const startTime = cleanedSlot.split('-')[0].trim();
    return parseTimeAndFormat(webinarDateTime, startTime);
  } else if (cleanedSlot.match(/\d{1,2}(AM|PM)\s*-\s*\d{1,2}(AM|PM)/i)) {
    const [startTime, endTime] = cleanedSlot
      .split('-')
      .map((time) => time.trim());
    const formattedStartTime = getFormatTimeWithMinutes(startTime);
    return parseTimeAndFormat(webinarDateTime, formattedStartTime);
  }

  const parts = cleanedSlot.split('-');
  const timeString = parts[parts.length - 1].trim();
  return parseTimeAndFormat(webinarDateTime, timeString);
};

export const getWeekDay = (weekNumber: number) => {
  switch (weekNumber) {
    case 0:
      return 'Sunday';
    case 1:
      return 'Monday';
    case 2:
      return 'Tuesday';
    case 3:
      return 'Wednesday';
    case 4:
      return 'Thrusday';
    case 5:
      return 'Friday';
    case 6:
      return 'Saturday';
    default:
      return '';
  }
};

export const getTimeZone = () =>
  new window.Intl.DateTimeFormat().resolvedOptions().timeZone;

const setHoursToZero = (date: Date) => {
  return date.setHours(0, 0, 0, 0);
};

export const isDateWithInRange = (
  date: Date,
  startDate: Date,
  endDate: Date
) => {
  const minDate = setHoursToZero(startDate);
  const maxDate = setHoursToZero(endDate);
  const currentDate = setHoursToZero(date);
  return isWithinInterval(currentDate, { start: minDate, end: maxDate });
};

export const getParsedDateFormat = (date: string) => {
  const parsedDate = parse(date, 'dd/MM/yyyy', new Date());
  return parsedDate;
};

export interface DateOption {
  label: string;
  value: string;
}

interface DateSelectionOption extends DateOption {
  max_visibility_time?: string;
  should_show_next_week?: boolean;
  condition?: string;
}

const getNextDateForDayCode = (dayCode: number, fromDate: Date): Date => {
  const fromDay = fromDate.getDay();
  const offset = (dayCode - fromDay + 7) % 7 || 7;
  return addDays(fromDate, offset);
};

const getExtraDatesToShowSlotsOnNextWeek = (
  slotOptions
): DateSelectionOption[] => {
  const today = new Date();
  const todayDayCode = today.getDay();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });

  const result: DateSelectionOption[] = [];
  const alreadyHandled = new Set<number>();
  const isTodaySunday = todayDayCode === 0;

  slotOptions.forEach((option) => {
    if (!option.should_show_next_week) return;

    const slotDate = getParsedDateFormat(option.value);
    const dayCode = getDay(slotDate);

    if (alreadyHandled.has(dayCode)) return;

    const weekdayInThisWeek = addDays(weekStart, (dayCode + 7 - 1) % 7);
    const isAfterToday = isAfter(weekdayInThisWeek, today);
    const isSlotSunday = dayCode === 0;

    // Show next week if: today is Sunday OR slot is Sunday OR this weekday has passed
    const shouldShowNextWeek = isTodaySunday || isSlotSunday || !isAfterToday;

    if (shouldShowNextWeek) {
      const nextDate = getNextDateForDayCode(dayCode, today);
      result.push({
        label: getFormattedDayAndMonthWithOrdinalSuffix(nextDate),
        value: format(nextDate, 'dd/MM/yyyy')
      });
      alreadyHandled.add(dayCode);
    }
  });

  return result;
};

export const filterDateOptionsByVisibility = (
  options: DateSelectionOption[],
  defaultMaxVisibilityTime?: string
): DateSelectionOption[] => {
  const today = new Date();
  const todayStart = startOfDay(today);

  const filteredOptions = options.filter((option) => {
    // Parse the option's date value
    const optionDate = parse(option.value, 'dd/MM/yyyy', new Date());
    const optionStart = startOfDay(optionDate);

    // If the date is in the future, include it
    if (isAfter(optionStart, todayStart)) {
      return true;
    }

    // If it's today, check max visibility time
    if (optionStart.getTime() === todayStart.getTime()) {
      // Use option's max_visibility_time if available, otherwise use default
      const maxVisibilityTime =
        (option as DateSelectionOption).max_visibility_time ||
        defaultMaxVisibilityTime;

      if (!maxVisibilityTime) return true;

      // Parse time in HH:mm format and set it to today's date
      const [hours, minutes] = maxVisibilityTime.split(':').map(Number);
      const visibilityDateTime = setMinutes(setHours(today, hours), minutes);

      // Include if current time is before or equal to max visibility time
      return !isAfter(today, visibilityDateTime);
    }

    return false;
  });

  const formattedOptions =
    filteredOptions.length === 0
      ? getExtraDatesToShowSlotsOnNextWeek(options)
      : filteredOptions.map((option) => {
        return {
          label: option.label,
          value: option.value,
          ...(option.condition && { condition: option.condition })
        };
      });

  return formattedOptions.sort((a, b) => {
    const dateA = new Date(a.value);
    const dateB = new Date(b.value);
    return dateA.getTime() - dateB.getTime();
  });
};

const getOrdinalSuffix = (day) => {
  if (day >= 11 && day <= 13) return 'th';
  const lastDigit = day % 10;
  switch (lastDigit) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const getFormattedDayAndMonthWithOrdinalSuffix = (
  date: Date
): string => {
  const day = format(date, 'd');
  const month = format(date, 'MMMM');
  return `${day}${getOrdinalSuffix(Number(day))} ${month}`;
};

export const getFormattedDateWithOrdinalSuffix = (date: Date): string => {
  const day = format(date, 'd');
  const month = format(date, 'MMMM');
  const time = format(date, 'h:mm a');
  return `${day}${getOrdinalSuffix(Number(day))} ${month}, ${time}`;
};

export const getDurationInSeconds = (startDate: Date, endDate: Date) => {
  const duration = differenceInSeconds(endDate, startDate);
  return duration;
};

/**
 * Returns the time difference in hours between now and the given ISO datetime string.
 * Example: -2.5 means the given time is 2 hours and 30 minutes in the past.
 *
 * @param {string} isoDateTime - An ISO 8601 datetime string (e.g., "2025-05-29T15:15:06.063Z")
 * @returns {number} The time difference in hours, as a float (positive = future, negative = past)
 */
export const getTimeDifferenceInHours = (isoDateTime: string): number => {
  const inputDate = parseISO(isoDateTime);
  const now = new Date();
  const diffInMinutes = differenceInMinutes(inputDate, now);
  return +(diffInMinutes / 60).toFixed(2); // round to 2 decimal places
};

/**
 * Converts a human-readable date string into an ISO-formatted date string.
 *
 * This function handles the following input formats:
 * - `'19th June, 7 PM'` → with 12-hour time format (no minutes)
 * - `'20th June, 7:00 PM'` → with 12-hour time format (with minutes)
 * - `'21st June'` → date only, no time
 *
 * If the input does not include a year, the current year is automatically appended.
 *
 * @param input - A string representing a date in one of the supported formats.
 * @returns An ISO 8601 formatted date string (e.g., `'2025-06-19T13:30:00.000Z'`)
 *
 * @throws Will throw an error if the input cannot be parsed into a valid date.
 *
 * @example
 * ```ts
 * convertToISO("19th June, 7 PM");
 * // → "2025-06-19T13:30:00.000Z" (depends on your timezone offset)
 *
 * convertToISO("20th June, 7:00 PM");
 * // → "2025-06-20T13:30:00.000Z"
 *
 * convertToISO("21st June");
 * // → "2025-06-21T00:00:00.000Z"
 * ```
 */
export const convertToISO = (input: string): string => {
  const currentYear = new Date().getFullYear();
  const inputWithYear = `${input} ${currentYear}`;

  const formatsToTry = [
    'do MMMM, h aa yyyy', // e.g., "19th June, 7 PM"
    'do MMMM, h:mm aa yyyy', // e.g., "20th June, 7:00 PM"
    'do MMMM yyyy' // e.g., "21st June"
  ];

  for (const formatStr of formatsToTry) {
    try {
      const parsedDate = parse(inputWithYear, formatStr, new Date());
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString();
      }
    } catch {
      // Try the next format
    }
  }

  throw new Error('Unable to parse input date: ' + input);
};

/**
 * Returns the next occurrence of a given weekday at the specified time (24-hour format).
 * @param day - Day of week: 0 = Sunday, 1 = Monday, ... 6 = Saturday (same as Date.getDay())
 * @param hours - Hour in 24-hour format (0-23)
 * @param mins - Minute (0-59), default 0
 * @param sec - Second (0-59), default 0
 */
export const getNextOccurrenceOfDayAtTime = (
  day: number,
  hours: number,
  mins = 0,
  sec = 0
): Date => {
  const now = getTodayDate();
  const currentDay = now.getDay();
  let daysUntilTarget = (day - currentDay + 7) % 7;

  if (daysUntilTarget === 0) {
    const isPastTime =
      now.getHours() > hours ||
      (now.getHours() === hours && now.getMinutes() > mins) ||
      (now.getHours() === hours &&
        now.getMinutes() === mins &&
        now.getSeconds() >= sec);
    if (isPastTime) daysUntilTarget = 7;
  }

  const next = new Date(now);
  next.setDate(now.getDate() + daysUntilTarget);
  next.setHours(hours, mins, sec, 0);
  return next;
};

export interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Returns the time left from fromDate until targetDate as days, hours, minutes, and seconds.
 * @param targetDate - The target date/time to count down to
 * @param fromDate - The reference date (defaults to current date via getTodayDate())
 * @returns Object with days, hours, minutes, seconds (all non-negative; zero when target is in the past)
 */
export const getTimeLeftUntil = (
  targetDate: Date,
  fromDate?: Date
): TimeLeft => {
  const from = fromDate ?? getTodayDate();
  const diffMs = targetDate.getTime() - from.getTime();
  const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const remainder = totalSeconds % 86400;
  const hours = Math.floor(remainder / 3600);
  const minutes = Math.floor((remainder % 3600) / 60);
  const seconds = remainder % 60;
  return { days, hours, minutes, seconds };
};
